import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useConnectionState } from '@livekit/components-react';
import { ConnectionQuality, ConnectionState } from 'livekit-client';

import { isWeak } from '../callSignals';
import { RemoteSignals } from '../types';
import { DS } from '../designTokens';

type Status = {
  titleKey: string;
  subKey?: string;
};

type Signals = {
  reconnecting: boolean;
  localWeak: boolean;
  remoteWeak: boolean;
  remoteCameraOff: boolean;
  remoteMuted: boolean;
};

// The design shows one pill at a time: "Ansluten" is replaced by the most
// pressing problem.
export const statusFor = (signals: Signals): Status => {
  if (signals.reconnecting) {
    return { titleKey: 'videoVisit.status.reconnecting' };
  }
  if (signals.remoteWeak) {
    return {
      titleKey: 'videoVisit.pill.remoteWeak',
      subKey: 'videoVisit.pill.remoteWeakSub',
    };
  }
  if (signals.remoteCameraOff && signals.remoteMuted) {
    return {
      titleKey: 'videoVisit.pill.remoteAllOff',
      subKey: 'videoVisit.pill.remoteAllOffSub',
    };
  }
  if (signals.remoteCameraOff) {
    return {
      titleKey: 'videoVisit.pill.audioOnly',
      subKey: 'videoVisit.pill.audioOnlySub',
    };
  }
  if (signals.remoteMuted) {
    return {
      titleKey: 'videoVisit.pill.remoteMuted',
      subKey: 'videoVisit.pill.remoteMutedSub',
    };
  }
  if (signals.localWeak) {
    return {
      titleKey: 'videoVisit.pill.localWeak',
      subKey: 'videoVisit.pill.localWeakSub',
    };
  }
  return { titleKey: 'videoVisit.pill.connected' };
};

const Dot = ({ color }: { color: string }) => (
  <Box
    component="span"
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: color,
      flex: 'none',
    }}
  />
);

const Pill = ({ status }: { status: Status }) => {
  const { t } = useTranslation();
  const isWarning = status.titleKey !== 'videoVisit.pill.connected';

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: 'absolute',
        left: 18,
        top: 18,
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '2px',
        maxWidth: 220,
        px: isWarning ? '14px' : '13px',
        py: isWarning ? '8px' : '7px',
        borderRadius: isWarning ? '16px' : '999px',
        bgcolor: isWarning ? DS.pillStrong : DS.pill,
        color: DS.linen,
        fontFamily: DS.fontBody,
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          whiteSpace: 'nowrap',
          fontSize: isWarning ? 12 : 11.5,
          fontWeight: 700,
        }}
      >
        <Dot color={isWarning ? DS.dawn : DS.freshTeal} />
        {t(status.titleKey)}
      </Box>
      {status.subKey && (
        <Box
          component="span"
          sx={{
            pl: 2,
            fontSize: 11.5,
            lineHeight: 1.35,
            color: DS.onDarkMuted,
          }}
        >
          {t(status.subKey)}
        </Box>
      )}
    </Box>
  );
};

type Props = {
  localQuality: ConnectionQuality;
  remote?: RemoteSignals;
};

const StatusPill = ({ localQuality, remote }: Props) => {
  const reconnecting = useConnectionState() === ConnectionState.Reconnecting;

  // Before the patient is in the room only our own reconnect is worth a pill;
  // the frame already says who we are waiting for.
  if (!remote) {
    if (!reconnecting) return null;
    return <Pill status={{ titleKey: 'videoVisit.status.reconnecting' }} />;
  }

  return (
    <Pill
      status={statusFor({
        reconnecting,
        localWeak: isWeak(localQuality),
        remoteWeak: isWeak(remote.quality),
        remoteCameraOff: remote.cameraOff,
        remoteMuted: remote.micOff,
      })}
    />
  );
};

export default StatusPill;
